import { useEffect, useRef, useState, type CSSProperties, type PointerEvent } from 'react';
import {
  motion,
  useInView,
  useMotionValue,
  useMotionTemplate,
  useReducedMotion,
  useSpring,
} from 'motion/react';
import { Pause, Play } from 'lucide-react';
import './luxury-motion.css';

type SceneStyle = CSSProperties & { '--slice'?: number; '--facet'?: number };
const layers = Array.from({ length: 11 }, (_, index) => index);

function SculptedRing({ material }: { material: 'pearl' | 'cobalt' | 'gold' }) {
  return (
    <div className={`lux-ring lux-ring--${material}`}>
      <div className="lux-ring-volume">
        {layers.map((index) => (
          <i key={index} className="lux-ring-slice" style={{ '--slice': index } as SceneStyle} />
        ))}
        <i className="lux-ring-face" />
        <i className="lux-ring-reflection" />
      </div>
    </div>
  );
}

function Monolith({ position }: { position: 'left' | 'center' | 'right' }) {
  return (
    <div className={`lux-monolith lux-monolith--${position}`}>
      <i className="lux-block-front" />
      <i className="lux-block-back" />
      <i className="lux-block-side" />
      <i className="lux-block-top" />
      <i className="lux-block-line" />
    </div>
  );
}

/** Decorative CSS sculpture with an accessible motion control; no network assets. */
export function LuxuryScene({
  variant = 'hero',
  className = '',
}: {
  variant?: 'hero' | 'login';
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { amount: 0.12 });
  const reducedMotion = useReducedMotion();
  const [manuallyPaused, setManuallyPaused] = useState(false);
  const paused = manuallyPaused || Boolean(reducedMotion);
  const rotateX = useSpring(0, { stiffness: 75, damping: 22, mass: 1.3 });
  const rotateY = useSpring(0, { stiffness: 75, damping: 22, mass: 1.3 });
  const lightX = useMotionValue(52);
  const lightY = useMotionValue(40);
  const aura = useMotionTemplate`radial-gradient(ellipse at ${lightX}% ${lightY}%, rgba(69,107,255,.2), transparent 62%)`;
  useEffect(() => {
    if (paused) {
      rotateX.stop();
      rotateY.stop();
    }
  }, [paused, rotateX, rotateY]);
  function move(event: PointerEvent<HTMLDivElement>) {
    if (
      paused ||
      event.pointerType === 'touch' ||
      !window.matchMedia('(hover: hover) and (pointer: fine)').matches
    )
      return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width));
    const y = Math.min(1, Math.max(0, (event.clientY - bounds.top) / bounds.height));
    rotateX.set((0.5 - y) * 11);
    rotateY.set((x - 0.5) * 15);
    lightX.set(40 + x * 24);
    lightY.set(30 + y * 20);
  }
  function reset() {
    if (paused) return;
    rotateX.set(0);
    rotateY.set(0);
    lightX.set(52);
    lightY.set(40);
  }
  return (
    <div
      ref={ref}
      className={`luxury-scene luxury-scene--${variant} ${className}`}
      data-animate={inView && !paused ? 'true' : 'false'}
    >
      <div
        className="lux-scene-artwork"
        aria-hidden="true"
        onPointerMove={move}
        onPointerLeave={reset}
        onPointerCancel={reset}
      >
        <motion.div className="lux-scene-aura" style={{ background: aura }} />
        <div className="lux-scene-grid" />
        <div className="lux-scene-halo lux-scene-halo--outer" />
        <div className="lux-scene-halo lux-scene-halo--inner" />
        <div className="lux-scene-perspective">
          <motion.div
            className="lux-scene-parallax"
            style={{ rotateX: reducedMotion ? 0 : rotateX, rotateY: reducedMotion ? 0 : rotateY }}
          >
            <div className="lux-scene-composition">
              <div className="lux-ground-shadow" />
              <div className="lux-plinth">
                <i className="lux-plinth-base" />
                <i className="lux-plinth-surface" />
                <i className="lux-plinth-trace" />
              </div>
              <div className="lux-architecture">
                <Monolith position="left" />
                <Monolith position="center" />
                <Monolith position="right" />
              </div>
              <div className="lux-sculpture">
                <SculptedRing material="cobalt" />
                <SculptedRing material="gold" />
                <SculptedRing material="pearl" />
                <span className="lux-orbit-bead lux-orbit-bead--pearl" />
                <span className="lux-orbit-bead lux-orbit-bead--gold" />
                <span className="lux-orbit-bead lux-orbit-bead--blue" />
              </div>
              <div className="lux-scene-measure lux-scene-measure--one">
                <i />
                <i />
                <i />
                <i />
              </div>
              <div className="lux-scene-measure lux-scene-measure--two">
                <i />
                <i />
                <i />
                <i />
              </div>
              <span className="lux-spark lux-spark--one" />
              <span className="lux-spark lux-spark--two" />
            </div>
          </motion.div>
        </div>
        <div className="lux-scene-vignette" />
      </div>
      {!reducedMotion && (
        <button
          type="button"
          className="lux-scene-toggle"
          aria-label={manuallyPaused ? 'Resume decorative animation' : 'Pause decorative animation'}
          title={manuallyPaused ? 'Resume decorative animation' : 'Pause decorative animation'}
          onClick={() => setManuallyPaused((current) => !current)}
        >
          {manuallyPaused ? (
            <Play size={13} aria-hidden="true" />
          ) : (
            <Pause size={13} aria-hidden="true" />
          )}
        </button>
      )}
    </div>
  );
}
